import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress
} from "@mui/material"
import { Close, DeleteOutline } from "@mui/icons-material"
import {
    useGetManageCourseReviewsQuery,
    useDeleteReviewMutation
} from "../../Redux/actions/lecture.action"
import RatingSummary from "../components/RatingSummary"
import ReviewItem from "../components/ReviewItem"
import ConfirmDialog from "../components/ConfirmDialog"

const PAGE_SIZE = 10

/** One page of a course's reviews; pages share the RTK Query cache. */
function ReviewPage({ course_id, page, onDelete }) {
    const { data } = useGetManageCourseReviewsQuery({
        course_id,
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE
    })
    return _.get(data, "list", []).map((review) => (
        <ReviewItem
            key={review.review_id}
            review={review}
            actions={
                <Tooltip title="Delete review">
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(review)}
                    >
                        <DeleteOutline fontSize="small" />
                    </IconButton>
                </Tooltip>
            }
        />
    ))
}

ReviewPage.propTypes = {
    course_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    page: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired
}

/**
 * The owner's view of one course's reviews: rating summary, every review
 * (newest first, "load more") and moderation -- deleting a review removes it
 * from the course's average too.
 */
export default function CourseReviewsDialog({ open, course, onClose }) {
    const course_id = course?.course_id
    const [pages, setPages] = React.useState(1)
    const [pendingDelete, setPendingDelete] = React.useState(null)
    const [error, setError] = React.useState("")

    React.useEffect(() => {
        setPages(1)
        setError("")
        setPendingDelete(null)
    }, [course_id])

    const firstPage = useGetManageCourseReviewsQuery(
        { course_id, offset: 0, limit: PAGE_SIZE },
        { skip: !open || !course_id }
    )
    const summary = firstPage.data || {}
    const total = summary.total || 0

    const [deleteAction, deleteState] = useDeleteReviewMutation()

    const confirmDelete = async () => {
        const review = pendingDelete
        setError("")
        try {
            await deleteAction({ review_id: review.review_id }).unwrap()
        } catch (failure) {
            setError(
                _.get(failure, "data.detail", null) || "Could not delete the review."
            )
        } finally {
            setPendingDelete(null)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            /* Full screen below sm so the list is readable on a phone. */
            sx={{
                "& .MuiDialog-paper": {
                    m: { xs: 0, sm: 4 },
                    width: { xs: "100%", sm: "auto" },
                    maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
                    height: { xs: "100%", sm: "auto" },
                    borderRadius: { xs: 0, sm: 2 }
                }
            }}
        >
            <DialogTitle sx={{ pr: 6 }}>
                Reviews
                {course?.title && (
                    <span className="mt-0.5 block text-xs font-normal text-slate-500">
                        {course.title}
                    </span>
                )}
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {firstPage.isLoading && (
                    <div className="flex justify-center py-10">
                        <CircularProgress size={28} />
                    </div>
                )}

                {firstPage.isError && (
                    <Alert severity="error">
                        {_.get(firstPage, "error.data.detail", null) ||
                            "Reviews could not be loaded."}
                    </Alert>
                )}

                {firstPage.isSuccess && (
                    <div className="space-y-4">
                        <RatingSummary
                            average={summary.rating_average}
                            count={summary.rating_count}
                            distribution={summary.distribution}
                        />

                        {error && <Alert severity="error">{error}</Alert>}

                        {total === 0 ? (
                            <p className="py-6 text-center text-sm text-slate-500">
                                No reviews for this course yet.
                            </p>
                        ) : (
                            <div>
                                {_.range(pages).map((page) => (
                                    <ReviewPage
                                        key={page}
                                        course_id={course_id}
                                        page={page}
                                        onDelete={setPendingDelete}
                                    />
                                ))}
                            </div>
                        )}

                        {pages * PAGE_SIZE < total && (
                            <div className="flex justify-center">
                                <Button size="small" onClick={() => setPages(pages + 1)}>
                                    Load more reviews
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete this review?"
                message={
                    pendingDelete
                        ? `${pendingDelete.author_name || "This student"}'s review will be removed and the course rating recalculated.`
                        : ""
                }
                busy={deleteState.isLoading}
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </Dialog>
    )
}

CourseReviewsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    course: PropTypes.object,
    onClose: PropTypes.func.isRequired
}
